import {
  SIGN_OUT_CAUSE,
  takePendingSignOutCause,
  trackSignOut
} from '#~/analytics/domains/sign-out-lifecycle.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import { track as trackAnalytics } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { useAuth0 } from '#~/auth/index.js';
import type { LocationState } from '#~/auth/index.js';
import { fail } from '#~/auth/index.js';
import { clearActiveAuthJourney } from '#~/auth/index.js';
import { PostHogManager } from '#~/posthog/index.js';
import { recordSignupBrowserReset } from '#~/auth/signup-posthog.js';
import { Paths } from '#~/routes/index.js';
import { clearPrequalResponses } from '#~/routes/register/prequal-storage.js';
import { Loader } from '@studio/design-system/loader';
import { toString } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Module-level adapter from (name, category, props) → AnalyticsEvent, the same
// shape auth/provider.tsx uses for its auth-journey tracker.
const trackSignOutEvent: AnalyticsTrackFn = (name, category, properties) => {
  void trackAnalytics({ category, name, properties } as AnalyticsEvent);
};

const Inner = ({ returnTo }: { returnTo: string }): ReactElement => {
  const { logout } = useAuth0();
  const logoutStarted = useRef(false);

  useEffect(() => {
    try {
      // Suppress duplicate effects for this visit, while allowing a later interrupted-logout retry.
      if (logoutStarted.current) {
        return;
      }
      logoutStarted.current = true;

      clearActiveAuthJourney();

      // ENG-4874: record the sign-out while the PostHog person is still this
      // user; reset() below detaches the distinct id. Every chosen sign-out
      // (account menu, Log out links, delete account) lands here, so the
      // person chose it unless a detector left a cause before navigating.
      trackSignOut(trackSignOutEvent, {
        cause: takePendingSignOutCause() ?? SIGN_OUT_CAUSE.USER
      });

      // Retain signup provenance before the SDK resets its first-touch cookie.
      recordSignupBrowserReset();
      PostHogManager.getInstance().reset();
      clearPrequalResponses();

      logout({
        logoutParams: {
          returnTo
        }
      });
    } catch (error) {
      console.error('Failed to logout:', error);

      throw fail('22AF', error as Error);
    }
  }, []);

  return <Loader />;
};

/** `<Logout>` component. */
export const Logout = (): ReactElement => {
  // Where will redirect after logout?
  const location = useLocation();
  const urlBase = `${window.location.protocol}//${window.location.host}`;

  const redirectUri = toString((location.state as LocationState)?.redirect, { default: '/login' });
  const returnTo = redirectUri.replace(urlBase, '');

  return <Inner returnTo={`${urlBase}/${Paths.login}#r=${returnTo}`} />;
};
